import { isArray, isNullOrUndefined } from 'app/shared/utils';

function checkValues(values: number[]): boolean {
    if (!values) {
        return false;
    }

    if (!isArray(values)) {
        return false;
    }

    return values.length !== 0;
}

export const mean = (values: number[]): number => {
    if (!checkValues(values)) {
        return undefined;
    }

    let aggVal = 0;
    let count = 0;
    for (let i = 0; i < values.length; ++i) {
        const value = values[i];
        if (value === null || typeof value === 'undefined') {
            continue;
        }
        aggVal += value;
        ++count;
    }

    if (count === 0) {
        return null;
    }

    return aggVal / count;
};

export const max = (values: number[]): number => {
    if (!checkValues(values)) {
        return undefined;
    }

    let val = Number.MIN_SAFE_INTEGER;
    for (let i = 0; i < values.length; ++i) {
        if (!values[i] && values[i] !== 0) {
            continue;
        }

        if (val < values[i]) {
            val = values[i];
        }
    }

    if (val === Number.MIN_SAFE_INTEGER) {
        return null;
    }

    return val;
};

export const min = (values: number[]): number => {
    if (!checkValues(values)) {
        return undefined;
    }

    let val = Number.MAX_SAFE_INTEGER;
    for (let i = 0; i < values.length; ++i) {
        if (!values[i] && values[i] !== 0) {
            continue;
        }

        if (val > values[i]) {
            val = values[i];
        }
    }

    if (val === Number.MAX_SAFE_INTEGER) {
        return null;
    }

    return val;
};

export const mode = (values: number[]): number => {
    if (!checkValues(values)) {
        return undefined;
    }

    const counts = {};

    // Create object with counts of each distinct value for non-null values
    for (let i = 0; i < values.length; i++) {
        if (values[i] !== null) {
            if (typeof counts[values[i]] === 'undefined') {
                counts[values[i]] = { value: values[i], count: 1 };
            } else {
                counts[values[i]].count += 1;
            }
        }
    }

    // Get value with largest count
    const keys = Object.keys(counts);
    let max = Number.MIN_SAFE_INTEGER;
    let valueForMax = null;
    for (let i = 0; i < keys.length; i++) {
        if (counts[keys[i]].count > max) {
            max = counts[keys[i]].count;
            valueForMax = counts[keys[i]].value;
        }
    }

    return valueForMax;
};

export const median = (values: number[]): number => {
    if (!checkValues(values)) {
        return undefined;
    }
    const length = values.length;
    if (length === 1) {
        return values[0];
    }
    // sort in order (ascending)
    values.sort(function (a, b) {
        return a - b;
    });

    // pick middle if odd, else add the two closest and divide by 2

    // If we have an odd amount of values, return the middle number.
    if (length % 2 === 1) {
        return values[length / 2 - 0.5];
    }

    // Else, return the value of the two middle numbers
    return (values[length / 2 - 1] + values[length / 2]) / 2;
};

const tenLogPrep = (values: number[]): number[] => {
    if (!checkValues(values)) {
        return undefined;
    }

    const intermediateValues = values
        .filter((x: number) => {
            return !isNullOrUndefined(x);
        })
        .map((x: number) => {
            return 10 ** (x / 10);
        });

    if (!intermediateValues.length) {
        return undefined;
    }

    return intermediateValues;
};

// ref.: Functions.xlsx from GHD; required to average noise measurements
export const tenLogMean = (values: number[]): number => {
    const intermediateValues = tenLogPrep(values);
    if (!intermediateValues) {
        return undefined;
    }

    const meanValue = mean(intermediateValues);

    return 10 * Math.log10(meanValue);
};

// ref.: Functions.xlsx from GHD; required to sum noise measurements
export const tenLogSum = (values: number[]): number => {
    const intermediateValues = tenLogPrep(values);
    if (!intermediateValues) {
        return undefined;
    }

    const sumValue = intermediateValues.reduce((a, b) => a + b, 0);

    return 10 * Math.log10(sumValue);
};
